import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchProducts, formatTT } from "../lib/api";
import ProductCard from "../components/ProductCard";
import { ArrowUpRight, TrendingDown, ShieldCheck, Calculator as CalcIcon } from "lucide-react";

const CATS = [
    { id: "personal", label: "Personal Loans", type: "loan", desc: "Quick cash for life events." },
    { id: "auto", label: "Auto Loans", type: "loan", desc: "Finance new or used vehicles." },
    { id: "mortgage", label: "Mortgages", type: "loan", desc: "Home purchase & refinance." },
    { id: "business", label: "Business / SME", type: "loan", desc: "Growth capital for TT businesses." },
    { id: "credit_card", label: "Credit Cards", type: "credit_card", desc: "Cashback, travel & rewards." },
];

const TICKER = [
    "Lowest Mortgage Rate: 7.50% APR · Scotiabank T&T",
    "Lowest SME Rate: 7.00% · Development Bank of T&T",
    "Travel Premium Card: Scotia Visa Infinite",
    "No-Credit-Check Prepaid: JMMB Visa",
    "Personal Loan from 11.50% · First Citizens Bank",
    "Avg. TT lending rate Q1 2026: 8.2%",
];

const Home = () => {
    const [featured, setFeatured] = useState([]);
    const [stats, setStats] = useState({ products: 21, banks: 7 });

    useEffect(() => {
        fetchProducts({ sort: "rating_desc" }).then((items) => {
            setFeatured(items.slice(0, 3));
            const banks = new Set(items.map((p) => p.provider)).size;
            setStats({ products: items.length, banks });
        });
    }, []);

    return (
        <div>
            {/* Hero — bento-grid */}
            <section className="border-b border-[#0B192C]/10">
                <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-24 grid lg:grid-cols-12 gap-8">
                    <div className="lg:col-span-8 flex flex-col justify-between">
                        <div>
                            <div className="label-overline text-[#1E3A2B] mb-6">Index № 01 — Trinidad & Tobago</div>
                            <h1 className="font-serif-display text-5xl md:text-6xl lg:text-7xl tracking-tight leading-[0.95] mb-6">
                                The honest ledger<br />
                                <em className="text-[#1E3A2B]">of loans & credit</em> in TT.
                            </h1>
                            <p className="text-base md:text-lg text-[#4A5568] max-w-xl leading-relaxed">
                                Compare interest rates, fees, and eligibility across Republic, RBC, First Citizens, Scotiabank and ANSA — side by side, no spin.
                            </p>
                        </div>
                        <div className="flex flex-wrap gap-3 mt-10">
                            <Link to="/products" data-testid="hero-browse-btn" className="btn-primary px-6 py-3 label-overline flex items-center gap-2">
                                Browse All Products <ArrowUpRight className="w-4 h-4" strokeWidth={1.5}/>
                            </Link>
                            <Link to="/calculator" data-testid="hero-calc-btn" className="px-6 py-3 label-overline border border-[#0B192C]/30 hover:border-[#0B192C] flex items-center gap-2">
                                Loan Calculator <CalcIcon className="w-4 h-4" strokeWidth={1.5}/>
                            </Link>
                        </div>
                    </div>

                    <div className="lg:col-span-4 grid grid-cols-2 gap-3">
                        <div className="grid-card p-5 col-span-2 bg-[#0A4F49] text-[#F8F7F4]">
                            <div className="label-overline opacity-70 mb-2">Lowest Rate Today</div>
                            <div className="font-serif-display text-4xl">7.00%</div>
                            <div className="text-xs opacity-80 mt-1 font-mono-body">DBdTT SME Loan · 15-yr</div>
                        </div>
                        <div className="grid-card p-5">
                            <div className="label-overline text-[#718096] mb-2">Products Indexed</div>
                            <div className="font-serif-display text-3xl" data-testid="hero-products-count">{stats.products}</div>
                        </div>
                        <div className="grid-card p-5">
                            <div className="label-overline text-[#718096] mb-2">Banks</div>
                            <div className="font-serif-display text-3xl" data-testid="hero-banks-count">{String(stats.banks).padStart(2, "0")}</div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Ticker */}
            <section className="border-b border-[#0B192C]/10 bg-[#EAE7E1] overflow-hidden">
                <div className="flex whitespace-nowrap py-3 ticker-track">
                    {[...TICKER, ...TICKER].map((t, i) => (
                        <div key={`ticker-${i}`} className="label-overline text-[#0B192C] px-8 flex items-center gap-3">
                            <TrendingDown className="w-3 h-3" strokeWidth={1.5} />
                            {t}
                        </div>
                    ))}
                </div>
            </section>

            {/* Categories */}
            <section className="max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-24">
                <div className="flex items-end justify-between mb-10">
                    <div>
                        <div className="label-overline text-[#718096] mb-2">№ 02 — Categories</div>
                        <h2 className="font-serif-display text-4xl md:text-5xl tracking-tight">What are you financing?</h2>
                    </div>
                    <Link to="/products" data-testid="see-all-link" className="hidden md:flex items-center gap-1 label-overline hover:text-[#1E3A2B]">
                        View all <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={1.5}/>
                    </Link>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
                    {CATS.map((c, i) => (
                        <Link
                            key={c.id}
                            to={`/products?${c.type === "credit_card" ? "type=credit_card" : `type=loan&category=${c.id}`}`}
                            data-testid={`category-${c.id}`}
                            className="grid-card p-8 flex flex-col justify-between min-h-[200px] group"
                        >
                            <div className="label-overline text-[#1E3A2B]">№ 0{i + 1}</div>
                            <div>
                                <h3 className="font-serif-display text-2xl mb-2 group-hover:underline decoration-[#D1A77E] underline-offset-4">{c.label}</h3>
                                <p className="text-xs text-[#4A5568]">{c.desc}</p>
                            </div>
                            <div className="flex justify-end">
                                <ArrowUpRight className="w-5 h-5 text-[#0B192C] group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" strokeWidth={1.5}/>
                            </div>
                        </Link>
                    ))}
                </div>
            </section>

            {/* Featured */}
            <section className="bg-[#EAE7E1]/40 border-y border-[#0B192C]/10">
                <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-24">
                    <div className="mb-10">
                        <div className="label-overline text-[#718096] mb-2">№ 03 — Top Rated</div>
                        <h2 className="font-serif-display text-4xl md:text-5xl tracking-tight">This week&rsquo;s best picks</h2>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {featured.map((p) => (
                            <ProductCard key={p.id} product={p} />
                        ))}
                    </div>
                </div>
            </section>

            {/* Trust strip */}
            <section className="max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-24 grid md:grid-cols-3 gap-12">
                <div>
                    <ShieldCheck className="w-7 h-7 mb-4 text-[#1E3A2B]" strokeWidth={1.25}/>
                    <h3 className="font-serif-display text-2xl mb-2">Independent.</h3>
                    <p className="text-sm text-[#4A5568] leading-relaxed">We aren&rsquo;t a lender and we don&rsquo;t take commissions per click. Our rankings rank what&rsquo;s best for you — not what pays us.</p>
                </div>
                <div>
                    <TrendingDown className="w-7 h-7 mb-4 text-[#1E3A2B]" strokeWidth={1.25}/>
                    <h3 className="font-serif-display text-2xl mb-2">TT-First Data.</h3>
                    <p className="text-sm text-[#4A5568] leading-relaxed">Rates and fees curated for the Trinidad & Tobago market. All figures in TT dollars. No US-centric estimates.</p>
                </div>
                <div>
                    <CalcIcon className="w-7 h-7 mb-4 text-[#1E3A2B]" strokeWidth={1.25}/>
                    <h3 className="font-serif-display text-2xl mb-2">Math First.</h3>
                    <p className="text-sm text-[#4A5568] leading-relaxed">Use our calculator to see your true monthly payment — including interest, processing fees, and tenure trade-offs.</p>
                </div>
            </section>
        </div>
    );
};

export default Home;
